import { ArrowDown, ArrowUp, Search } from "lucide-react";
import { useMemo, useState } from "react";
import type { University } from "../types";
import { searchUniversities } from "../utils/data";

type SortKey = keyof Pick<University, "num" | "name" | "code" | "department" | "location" | "level" | "remark">;

type DirectoryTableProps = {
  rows: University[];
  query: string;
  selectedUniversity: University | null;
  onQueryChange: (query: string) => void;
  onSelectUniversity: (university: University) => void;
};

const columns: Array<{ key: SortKey; label: string }> = [
  { key: "num", label: "序号" },
  { key: "name", label: "学校名称" },
  { key: "code", label: "标识码" },
  { key: "department", label: "主管部门" },
  { key: "location", label: "所在地" },
  { key: "level", label: "层次" },
  { key: "remark", label: "备注" }
];

function compareText(a: string, b: string) {
  return a.localeCompare(b, "zh-CN", { numeric: true, sensitivity: "base" });
}

export function DirectoryTable({
  rows,
  query,
  selectedUniversity,
  onQueryChange,
  onSelectUniversity
}: DirectoryTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const visibleRows = useMemo(() => {
    const searched = searchUniversities(rows, query);
    return [...searched].sort((a, b) => {
      const result = compareText(String(a[sortKey] ?? ""), String(b[sortKey] ?? ""));
      return sortDirection === "asc" ? result : -result;
    });
  }, [query, rows, sortDirection, sortKey]);

  const displayedRows = visibleRows.slice(0, 160);

  return (
    <section className="directory-shell" aria-labelledby="directory-title">
      <div className="section-heading">
        <div>
          <p className="eyebrow">REGISTRY</p>
          <h2 id="directory-title">高校目录</h2>
        </div>
        <div className="search-box">
          <Search size={16} />
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="搜索学校、城市、主管部门、备注"
            aria-label="搜索高校目录"
          />
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {columns.map((column) => {
                const active = sortKey === column.key;
                return (
                  <th key={column.key}>
                    <button
                      type="button"
                      onClick={() => {
                        if (active) {
                          setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
                        } else {
                          setSortKey(column.key);
                          setSortDirection("asc");
                        }
                      }}
                    >
                      <span>{column.label}</span>
                      {active &&
                        (sortDirection === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />)}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((row) => {
              const isSelected = selectedUniversity?.num === row.num;
              return (
                <tr
                  key={`${row.num}-${row.name}`}
                  className={isSelected ? "is-selected-row" : ""}
                  onClick={() => onSelectUniversity(row)}
                >
                  <td className="mono">{row.num}</td>
                  <td>
                    <button className="text-button" type="button">
                      {row.name}
                    </button>
                  </td>
                  <td className="mono">{row.code || "-"}</td>
                  <td>{row.department || "-"}</td>
                  <td>{row.location || "-"}</td>
                  <td>
                    <span className={`badge ${row.level === "本科" ? "badge-blue" : "badge-green"}`}>
                      {row.level || "未标注"}
                    </span>
                  </td>
                  <td>{row.remark ? <span className="badge badge-plain">{row.remark}</span> : "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="table-footer">
        显示 {displayedRows.length} 条，共 {visibleRows.length} 条匹配记录
      </div>
    </section>
  );
}
